import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  connectInjectedWallet, ensurePolygonMainnet, executeTenUsdtSplitPayment,
  getUSDTBalance, hasInjectedWallet, POLYGON_CHAIN_ID_DEC, SPLITTER_ADDR, USDT_ADDR, shortAddr,
} from "./web3Client";
import {
  Wallet, ShieldCheck, Zap, Copy, Sparkles, CreditCard, LogIn, ExternalLink, Bot,
  CheckCircle2, XCircle, Loader2, ChevronRight, Lock,
} from "lucide-react";
import AiHunterConsole from "../dashboard/AiHunterConsole";

const API = process.env.REACT_APP_BACKEND_URL || "";
const MOONPAY_ENABLED = !!(process.env.REACT_APP_MOONPAY_API_KEY || "").trim();
const WEB3AUTH_ENABLED = !!(process.env.REACT_APP_WEB3AUTH_CLIENT_ID || "").trim();

const MARKETING_TEXT =
  "Early Access Activation Fee: 10 / 50 / 250 USDT (Polygon) — Genesis Providers are FREE. " +
  "Upon Mainnet Launch, your tier's TGC Token Airdrop is unlocked with a 300% yield. " +
  "Once the maturation completes, the principal burns and you keep the clean net cash profit.";

const TIER_DEFS = {
  PROVIDER:            { label: "PROVIDER",   fee: 10,  airdrop: 100,  payout: 200,  color: "#00F2FF", tag: "Партнёр / Networker" },
  MERCHANT_RETAIL:     { label: "RETAIL",     fee: 50,  airdrop: 500,  payout: 1000, color: "#FFD700", tag: "Кафе / магазин / салон" },
  MERCHANT_ENTERPRISE: { label: "ENTERPRISE", fee: 250, airdrop: 2500, payout: 5000, color: "#BC13FE", tag: "Фабрика / клиника / корп." },
};

export default function OnboardingPage() {
  const [params] = useSearchParams();
  const ref = params.get("ref") || "";
  const [config, setConfig] = useState(null);
  const [wallet, setWallet] = useState(null);       // { address, provider, chainId }
  const [usdt, setUsdt] = useState(null);           // balance formatted
  const [connecting, setConnecting] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [paying, setPaying] = useState(false);
  const [mining, setMining] = useState(false);
  const [txHash, setTxHash] = useState("");
  const [status, setStatus] = useState(null);       // "success" | "error" | null
  const [errorMsg, setErrorMsg] = useState("");
  const [userId, setUserId] = useState(localStorage.getItem("tgc_fiat_user_id") || "");
  const [earnings, setEarnings] = useState(null);
  const [aiRecruiterOn, setAiRecruiterOn] = useState(false);
  const [tier, setTier] = useState(() => {
    // STEP 15 — tier priority: (1) URL ?tier= param (from paywall redirect),
    // (2) localStorage from registration, (3) fallback PROVIDER.
    const fromUrl = params.get("tier");
    if (fromUrl && ["PROVIDER", "MERCHANT_RETAIL", "MERCHANT_ENTERPRISE"].includes(fromUrl)) {
      try { localStorage.setItem("tgc_fiat_tier", fromUrl); localStorage.setItem("selected_registration_profile", fromUrl); } catch (e) { /* noop */ }
      return fromUrl;
    }
    return localStorage.getItem("tgc_fiat_tier") || localStorage.getItem("selected_registration_profile") || "PROVIDER";
  });
  const tierLocked = !!params.get("tier");    // if arrived via paywall, tier is locked
  const tierDef = TIER_DEFS[tier] || TIER_DEFS.PROVIDER;

  // Load public config once
  useEffect(() => {
    fetch(`${API}/api/tgc/fiat/config`)
      .then((r) => r.json()).then(setConfig).catch(() => {});
  }, []);

  // Load earnings live (15s) when user identified
  useEffect(() => {
    if (!userId) return;
    let alive = true;
    const load = () => fetch(`${API}/api/tgc/fiat/earnings/${userId}`)
      .then((r) => r.json()).then((j) => alive && setEarnings(j)).catch(() => {});
    load();
    const iv = setInterval(load, 15000);
    return () => { alive = false; clearInterval(iv); };
  }, [userId]);

  // Copy referral link — this user's ?ref=
  const shareLink = useMemo(() => {
    if (!userId) return "";
    return `${window.location.origin}/onboarding?ref=${userId}`;
  }, [userId]);

  const doConnect = async () => {
    setErrorMsg(""); setConnecting(true);
    try {
      const { provider, address } = await connectInjectedWallet();
      const net = await provider.getNetwork();
      const chainId = Number(net.chainId);
      setWallet({ provider, address, chainId });
      // auto-refresh balance
      try {
        const b = await getUSDTBalance(provider, address);
        setUsdt(b.formatted);
      } catch (_) { /* silent */ }
      // register in DB (idempotent)
      const r = await fetch(`${API}/api/tgc/fiat/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ wallet_address: address, parent_id: ref || null, is_genesis_provider: false, account_type: tier }),
      });
      const j = await r.json();
      if (j.user_id) {
        setUserId(j.user_id);
        localStorage.setItem("tgc_fiat_user_id", j.user_id);
        localStorage.setItem("tgc_fiat_tier", tier);
      }
    } catch (e) {
      setErrorMsg(e?.message || "Wallet connection failed");
    } finally { setConnecting(false); }
  };

  const doSwitchToPolygon = async () => {
    setErrorMsg(""); setSwitching(true);
    try {
      await ensurePolygonMainnet();
      // Refresh network in state
      const net = await wallet.provider.getNetwork();
      setWallet((w) => ({ ...w, chainId: Number(net.chainId) }));
    } catch (e) {
      setErrorMsg(e?.message || "Не удалось переключить сеть на Polygon");
    } finally { setSwitching(false); }
  };

  const doPay = async () => {
    if (!wallet) { setErrorMsg("Сначала подключите кошелёк"); return; }
    if (wallet.chainId !== POLYGON_CHAIN_ID_DEC) { setErrorMsg("Переключитесь на Polygon Mainnet"); return; }
    setErrorMsg(""); setStatus(null); setPaying(true); setMining(false);
    try {
      const amountUsdt = tierDef.fee;
      const tx = await executeTenUsdtSplitPayment({ provider: wallet.provider, referrerAddress: ref, amountUsdt });
      setPaying(false); setMining(true); setTxHash(tx.hash);
      const receipt = await tx.wait();  // typically 2-3 seconds on Polygon
      setMining(false);
      // Fire the webhook to sync payments_tracker
      const w = await fetch(`${API}/api/tgc/fiat/webhook/tx-confirmed`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: userId, tx_hash: receipt.hash, amount_usdt: amountUsdt, chain_id: POLYGON_CHAIN_ID_DEC }),
      });
      if (!w.ok) throw new Error((await w.json()).detail || "Webhook failed");
      setStatus("success");
    } catch (e) {
      setPaying(false); setMining(false);
      setStatus("error");
      setErrorMsg(e?.shortMessage || e?.message || "Payment failed");
    }
  };

  const wrongChain = wallet && wallet.chainId !== POLYGON_CHAIN_ID_DEC;

  return (
    <main data-testid="onboarding-page" style={{ minHeight: "100vh", background: "linear-gradient(180deg, #050914 0%, #0B1220 40%, #050914 100%)", color: "#E7ECFF", padding: "40px 20px" }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        {/* HERO */}
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "6px 14px", background: "rgba(255,215,0,0.08)", border: "1px solid #FFD700", borderRadius: 999, fontSize: 12, color: "#FFD700", letterSpacing: 1, textTransform: "uppercase", marginBottom: 16 }}>
            <Sparkles size={12} /> Early Access · Polygon Mainnet
          </div>
          <h1 style={{ fontSize: 42, margin: 0, lineHeight: 1.1 }}>
            Присоединиться к <span style={{ color: "#FFD700" }}>Turbo Global Coin</span>
          </h1>
          <p style={{ color: "#8B94B8", fontSize: 15, marginTop: 12, maxWidth: 620, margin: "12px auto 0" }}>
            Активируйте место в структуре за €10 через Web3 или банковской картой. Gas на Polygon ~ $0.01.
          </p>
        </div>

        {/* MARKETING BANNER */}
        <div data-testid="onboarding-marketing-banner"
          style={{ padding: 20, background: "linear-gradient(135deg, rgba(255,215,0,0.10), rgba(0,242,255,0.06))", border: "1px solid rgba(255,215,0,0.35)", borderRadius: 14, marginBottom: 28, display: "flex", gap: 14, alignItems: "flex-start" }}>
          <div style={{ padding: 10, background: "rgba(255,215,0,0.15)", borderRadius: 10, flexShrink: 0 }}>
            <Zap size={22} color="#FFD700" />
          </div>
          <div style={{ fontSize: 14, lineHeight: 1.55 }}>{MARKETING_TEXT}</div>
        </div>

        {/* REF banner */}
        {ref && (
          <div data-testid="onboarding-ref-banner"
            style={{ padding: 12, background: "rgba(0,242,255,0.06)", border: "1px solid rgba(0,242,255,0.3)", borderRadius: 10, marginBottom: 20, fontSize: 13, display: "flex", justifyContent: "space-between" }}>
            <span>🎯 Вас пригласил провайдер: <code style={{ background: "rgba(0,0,0,0.3)", padding: "2px 8px", borderRadius: 4 }}>{ref.slice(0, 8)}…</code></span>
            <span style={{ color: "#10F4A7" }}>44% каждого платежа идёт вверх по 8-уровневой цепочке</span>
          </div>
        )}

        {/* TIER SELECTOR — Step 11 */}
        <div data-testid="onboarding-tier-selector" style={{ marginBottom: 28 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
            <h3 style={{ margin: 0, fontSize: 15, color: "#8B94B8", textTransform: "uppercase", letterSpacing: 2 }}>
              Выберите тариф активации
            </h3>
            <span style={{ fontSize: 11, color: "#5F6884" }}>Airdrop и Cash-Payout масштабируются пропорционально тарифу</span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
            {Object.entries(TIER_DEFS).map(([k, def]) => {
              const active = tier === k;
              return (
                <button key={k} data-testid={`tier-${k.toLowerCase()}`}
                  onClick={() => { if (!tierLocked) { setTier(k); localStorage.setItem("tgc_fiat_tier", k); } }}
                  disabled={tierLocked && !active}
                  style={{
                    padding: 16, borderRadius: 12, cursor: (tierLocked && !active) ? "not-allowed" : "pointer",
                    opacity: (tierLocked && !active) ? 0.35 : 1,
                    background: active ? `linear-gradient(135deg, ${def.color}22, ${def.color}08)` : "#0B1220",
                    border: `1.5px solid ${active ? def.color : "rgba(255,255,255,0.08)"}`,
                    color: "#E7ECFF", textAlign: "left",
                    transition: "all 0.18s ease", position: "relative",
                  }}>
                  {active && (
                    <div style={{ position: "absolute", top: 8, right: 10, fontSize: 10, letterSpacing: 1, color: def.color, fontWeight: 800 }}>
                      ✓ SELECTED
                    </div>
                  )}
                  <div style={{ fontSize: 11, color: def.color, letterSpacing: 1, textTransform: "uppercase", fontWeight: 800 }}>{def.label}</div>
                  <div style={{ fontSize: 28, fontWeight: 900, marginTop: 6, color: def.color, fontFeatureSettings: '"tnum"' }}>${def.fee}</div>
                  <div style={{ fontSize: 11, color: "#8B94B8", marginTop: 2 }}>USDT · Polygon</div>
                  <div style={{ fontSize: 11, color: "#5F6884", marginTop: 8 }}>{def.tag}</div>
                  <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px dashed rgba(255,255,255,0.08)", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4, fontSize: 11 }}>
                    <div><span style={{ color: "#5F6884" }}>Airdrop:</span> <b style={{ color: "#10F4A7" }}>€{def.airdrop}</b></div>
                    <div><span style={{ color: "#5F6884" }}>Net payout:</span> <b style={{ color: "#FFD700" }}>€{def.payout}</b></div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 28 }}>
          {/* LEFT — WEB3 PAYMENT */}
          <section data-testid="onboarding-web3-box"
            style={{ padding: 24, background: "#0B1220", border: "1px solid rgba(0,242,255,0.25)", borderRadius: 14 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              <Wallet size={18} color="#00F2FF" />
              <h3 style={{ margin: 0, fontSize: 18, color: "#00F2FF" }}>Web3 Wallet</h3>
            </div>
            <div style={{ fontSize: 12, color: "#8B94B8", marginBottom: 18 }}>
              Оплата <b style={{ color: tierDef.color }}>{tierDef.fee} USDT</b> ({tierDef.label}) на Polygon (chain ID {POLYGON_CHAIN_ID_DEC}). Gas &lt; $0.01.
            </div>

            {/* Web3Auth social login placeholder */}
            <button
              disabled={!WEB3AUTH_ENABLED}
              onClick={() => alert("Настройте REACT_APP_WEB3AUTH_CLIENT_ID в .env для активации.")}
              data-testid="onboarding-social-signin"
              style={{
                width: "100%", padding: "12px 14px", borderRadius: 10, marginBottom: 12,
                background: "#fff", color: "#111", fontWeight: 600, fontSize: 14, border: "none",
                cursor: WEB3AUTH_ENABLED ? "pointer" : "not-allowed", opacity: WEB3AUTH_ENABLED ? 1 : 0.55,
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              }}>
              <LogIn size={16} /> {WEB3AUTH_ENABLED ? "Sign In with Google / Apple ID" : "Sign In with Google / Apple ID (требуется настройка)"}
            </button>
            <div style={{ textAlign: "center", fontSize: 11, color: "#5F6884", margin: "6px 0 14px" }}>
              — или подключите существующий кошелёк —
            </div>

            {/* Legacy wallet path — MetaMask/TrustWallet/etc */}
            {!wallet && (
              <button onClick={doConnect} disabled={connecting || !hasInjectedWallet()}
                data-testid="onboarding-connect-wallet"
                style={{
                  width: "100%", padding: "14px 16px", borderRadius: 10, marginBottom: 12,
                  background: "#00F2FF", color: "#050914", fontWeight: 700, fontSize: 14, border: "none",
                  cursor: hasInjectedWallet() ? "pointer" : "not-allowed", opacity: hasInjectedWallet() ? 1 : 0.55,
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                }}>
                {connecting ? <><Loader2 size={16} className="spin" /> Подключение…</> : <><Wallet size={16} /> Connect MetaMask / TrustWallet</>}
              </button>
            )}
            {!hasInjectedWallet() && (
              <div style={{ fontSize: 11, color: "#FF9F43", background: "rgba(255,159,67,0.08)", padding: 8, borderRadius: 6, marginBottom: 12 }}>
                Web3 wallet не найден. Установите <a href="https://metamask.io/download/" target="_blank" rel="noreferrer" style={{ color: "#FFD700" }}>MetaMask</a> или используйте DApp-браузер TrustWallet.
              </div>
            )}

            {wallet && (
              <div data-testid="onboarding-wallet-info"
                style={{ padding: 12, background: "rgba(0,242,255,0.05)", border: "1px solid rgba(0,242,255,0.25)", borderRadius: 10, marginBottom: 14, fontSize: 13 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                  <span style={{ color: "#8B94B8" }}>Кошелёк:</span>
                  <code style={{ color: "#00F2FF" }}>{shortAddr(wallet.address)}</code>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                  <span style={{ color: "#8B94B8" }}>Сеть:</span>
                  <span style={{ color: wrongChain ? "#FF3D6C" : "#10F4A7", fontWeight: 700 }}>
                    {wrongChain ? "Wrong network" : "Polygon Mainnet ✓"}
                  </span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ color: "#8B94B8" }}>USDT баланс:</span>
                  <span style={{ color: "#FFD700", fontWeight: 700 }}>{usdt !== null ? `${usdt.toFixed(2)} USDT` : "—"}</span>
                </div>
              </div>
            )}

            {wallet && wrongChain && (
              <button onClick={doSwitchToPolygon} disabled={switching} data-testid="onboarding-switch-network"
                style={{
                  width: "100%", padding: "12px 14px", borderRadius: 10, marginBottom: 12,
                  background: "#FF9F43", color: "#050914", fontWeight: 700, fontSize: 13, border: "none", cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                }}>
                {switching ? <><Loader2 size={14} className="spin" /> Переключение…</> : "Переключиться на Polygon Mainnet"}
              </button>
            )}

            {wallet && !wrongChain && (
              <button onClick={doPay} disabled={paying || mining || !SPLITTER_ADDR}
                data-testid="onboarding-pay-10-usdt"
                style={{
                  width: "100%", padding: "16px 18px", borderRadius: 10,
                  background: SPLITTER_ADDR ? "linear-gradient(135deg, #FFD700, #FF9F43)" : "#333", color: "#050914",
                  fontWeight: 800, fontSize: 15, border: "none",
                  cursor: SPLITTER_ADDR && !(paying || mining) ? "pointer" : "not-allowed",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                }}>
                {paying ? <><Loader2 size={16} className="spin" /> Подписание транзакции…</> :
                  SPLITTER_ADDR ? <><ChevronRight size={16} /> Оплатить {tierDef.fee} USDT ({tierDef.label})</> :
                    <><Lock size={14} /> Контракт не задеплоен</>}
              </button>
            )}
            {!SPLITTER_ADDR && wallet && (
              <div style={{ fontSize: 11, color: "#8B94B8", marginTop: 8, background: "rgba(255,159,67,0.05)", padding: 8, borderRadius: 6 }}>
                💡 Установите <code>REACT_APP_TGC_SPLITTER_CONTRACT</code> в <code>.env</code> после деплоя контракта Splitter в Polygon Mainnet.
              </div>
            )}
          </section>

          {/* RIGHT — FIAT ON-RAMP */}
          <section data-testid="onboarding-fiat-onramp"
            style={{ padding: 24, background: "#0B1220", border: "1px solid rgba(255,215,0,0.25)", borderRadius: 14 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              <CreditCard size={18} color="#FFD700" />
              <h3 style={{ margin: 0, fontSize: 18, color: "#FFD700" }}>Оплата картой Visa/Mastercard</h3>
            </div>
            <div style={{ fontSize: 12, color: "#8B94B8", marginBottom: 18 }}>
              Fiat-to-crypto шлюз конвертирует {tierDef.fee} USDT ({tierDef.label}) автоматически.
            </div>
            {MOONPAY_ENABLED ? (
              <iframe title="MoonPay"
                src={`https://buy.moonpay.com?apiKey=${encodeURIComponent(process.env.REACT_APP_MOONPAY_API_KEY)}&currencyCode=usdt_polygon&baseCurrencyCode=eur&baseCurrencyAmount=${tierDef.fee}&walletAddress=${wallet?.address || ""}`}
                style={{ width: "100%", height: 420, border: "none", borderRadius: 10, background: "#fff" }} />
            ) : (
              <div style={{ padding: 20, textAlign: "center", background: "rgba(255,215,0,0.04)", border: "1px dashed rgba(255,215,0,0.3)", borderRadius: 10 }}>
                <Lock size={26} color="#FFD700" style={{ marginBottom: 10 }} />
                <div style={{ fontSize: 13, marginBottom: 6, fontWeight: 700 }}>MoonPay / Ramp / Mercuryo widget</div>
                <div style={{ fontSize: 11, color: "#8B94B8", lineHeight: 1.5 }}>
                  Установите <code>REACT_APP_MOONPAY_API_KEY</code> (или Ramp/Mercuryo эквивалент) в <code>.env</code>.<br />
                  Виджет будет встроен сюда и автоматически конвертирует €10 в USDT Polygon.
                </div>
                <a href="https://www.moonpay.com/dashboard" target="_blank" rel="noreferrer"
                  style={{ display: "inline-flex", alignItems: "center", gap: 4, marginTop: 12, fontSize: 11, color: "#FFD700" }}>
                  Получить MoonPay API ключ <ExternalLink size={11} />
                </a>
              </div>
            )}
          </section>
        </div>

        {/* AI HUNTER CONSOLE — Step 11 */}
        {userId && (
          <div style={{ marginBottom: 28 }}>
            <AiHunterConsole limit={24} />
          </div>
        )}

        {/* STATUS / EARNINGS / REFERRAL LINK */}
        {userId && (
          <div data-testid="onboarding-earnings-section"
            style={{ padding: 24, background: "#0B1220", border: "1px solid rgba(16,244,167,0.25)", borderRadius: 14, marginBottom: 28 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, flexWrap: "wrap", gap: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Sparkles size={18} color="#10F4A7" />
                <h3 style={{ margin: 0, fontSize: 18, color: "#10F4A7" }}>Живой Cashflow (44% multi-level)</h3>
              </div>
              <button data-testid="onboarding-launch-ai-recruiter"
                onClick={() => setAiRecruiterOn(!aiRecruiterOn)}
                style={{
                  padding: "10px 14px", borderRadius: 999, fontSize: 12, fontWeight: 700,
                  background: aiRecruiterOn ? "linear-gradient(135deg, #BC13FE, #00F2FF)" : "transparent",
                  border: aiRecruiterOn ? "none" : "1px solid #BC13FE",
                  color: aiRecruiterOn ? "#fff" : "#BC13FE",
                  cursor: "pointer", display: "flex", alignItems: "center", gap: 6,
                }}>
                <Bot size={13} /> {aiRecruiterOn ? "AI Recruiter · ON" : "Launch AI Recruiter"}
              </button>
            </div>

            {/* Referral share link */}
            {shareLink && (
              <div style={{ display: "flex", gap: 8, marginBottom: 14, alignItems: "center", padding: 10, background: "rgba(0,242,255,0.05)", borderRadius: 8 }}>
                <span style={{ fontSize: 11, color: "#8B94B8" }}>Ваша реф-ссылка:</span>
                <code style={{ flex: 1, fontSize: 12, color: "#00F2FF", overflow: "hidden", textOverflow: "ellipsis" }}>{shareLink}</code>
                <button onClick={() => { navigator.clipboard.writeText(shareLink); }}
                  data-testid="onboarding-copy-ref"
                  style={{ padding: "4px 10px", borderRadius: 6, fontSize: 11, background: "transparent", border: "1px solid #00F2FF", color: "#00F2FF", cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}>
                  <Copy size={11} /> Copy
                </button>
              </div>
            )}

            {/* Stats */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 14 }}>
              <StatCard label="Прямых рефералов" value={earnings?.direct_referrals ?? 0} color="#00F2FF" />
              <StatCard label="Активаций (все уровни)" value={earnings?.payments_received ?? 0} color="#FFD700" />
              <StatCard label="Заработано (USDT)" value={`$${(earnings?.total_earned_usdt ?? 0).toFixed(2)}`} color="#10F4A7" />
            </div>

            {/* Per-level breakdown */}
            {earnings?.by_level && (
              <div data-testid="onboarding-by-level" style={{ display: "grid", gridTemplateColumns: "repeat(8, 1fr)", gap: 6, marginBottom: 14 }}>
                {[1,2,3,4,5,6,7,8].map((lvl) => {
                  const v = Number(earnings.by_level[String(lvl)] || 0);
                  return (
                    <div key={lvl} style={{ padding: "8px 4px", background: "rgba(0,242,255,0.05)", border: "1px solid rgba(0,242,255,0.15)", borderRadius: 6, textAlign: "center" }}>
                      <div style={{ fontSize: 9, color: "#5F6884", letterSpacing: 1 }}>L{lvl}</div>
                      <div style={{ fontSize: 12, color: v > 0 ? "#10F4A7" : "#5F6884", fontWeight: 700, fontFeatureSettings: '"tnum"' }}>
                        ${v.toFixed(2)}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Earnings table */}
            <div data-testid="onboarding-earnings-table" style={{ background: "rgba(0,0,0,0.3)", borderRadius: 10, overflow: "hidden" }}>
              <div style={{ padding: "10px 14px", borderBottom: "1px solid rgba(255,255,255,0.06)", display: "grid", gridTemplateColumns: "1.5fr 0.6fr 1fr 1fr 1.2fr", gap: 10, fontSize: 11, color: "#8B94B8", textTransform: "uppercase", letterSpacing: 1 }}>
                <div>Payer</div><div>Level</div><div>Total paid</div><div>Ваш cash</div><div>Tx / когда</div>
              </div>
              {(!earnings || earnings.rows?.length === 0) && (
                <div style={{ padding: 24, textAlign: "center", color: "#5F6884", fontSize: 13 }}>
                  Пока никто из вашей 8-уровневой структуры не оплатил активацию. Поделитесь ссылкой выше — L1 приносит 20%, L2 = 10%, L3 = 5%, L4 = 3%, L5/6 = 2%, L7/8 = 1%.
                </div>
              )}
              {earnings?.rows?.map((r) => (
                <div key={r.payment_id} style={{ padding: "10px 14px", borderBottom: "1px dashed rgba(255,255,255,0.05)", display: "grid", gridTemplateColumns: "1.5fr 0.6fr 1fr 1fr 1.2fr", gap: 10, fontSize: 12, alignItems: "center" }}>
                  <code style={{ color: "#00F2FF", fontSize: 11 }}>{String(r.from_payer || "").slice(0, 10)}…</code>
                  <div><span style={{ padding: "2px 8px", borderRadius: 999, background: "rgba(188,19,254,0.15)", color: "#BC13FE", fontWeight: 700, fontSize: 11 }}>L{r.level}</span></div>
                  <div>${Number(r.amount_total_paid || 0).toFixed(2)}</div>
                  <div style={{ color: "#10F4A7", fontWeight: 700 }}>+${Number(r.amount_usdt || 0).toFixed(2)}</div>
                  <div style={{ fontSize: 11, color: "#8B94B8", display: "flex", alignItems: "center", gap: 6 }}>
                    {r.timestamp ? new Date(r.timestamp).toLocaleString("ru-RU", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—"}
                    {r.tx_hash && <a href={`https://polygonscan.com/tx/${r.tx_hash}`} target="_blank" rel="noreferrer" style={{ color: "#00F2FF" }}><ExternalLink size={10} /></a>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Legal / trust footer */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#5F6884", fontSize: 11, justifyContent: "center", marginTop: 20 }}>
          <ShieldCheck size={12} /> Non-custodial · Contract-level 90/10 split enforced on-chain · Polygon Mainnet only
        </div>
      </div>

      {/* MINING STATUS OVERLAY */}
      {(mining || (status === "success" && txHash) || status === "error") && (
        <div data-testid="onboarding-mining-modal"
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100 }}>
          <div style={{ background: "#0B1220", border: `1px solid ${status === "error" ? "#FF3D6C" : status === "success" ? "#10F4A7" : "#00F2FF"}`, borderRadius: 16, padding: 32, maxWidth: 460, width: "90%", textAlign: "center" }}>
            {mining && (
              <>
                <Loader2 size={40} color="#00F2FF" className="spin" style={{ marginBottom: 16 }} />
                <div style={{ fontSize: 18, fontWeight: 700, color: "#00F2FF", marginBottom: 6 }}>[Web3] Routing via Polygon Ledger…</div>
                <div style={{ fontSize: 13, color: "#8B94B8", marginBottom: 14 }}>Processing {tierDef.fee} USDT Split ({tierDef.label}) · Block Settling…</div>
                <div style={{ fontSize: 11, color: "#5F6884" }}>Обычно 2–3 секунды на Polygon</div>
                {txHash && (
                  <a href={`https://polygonscan.com/tx/${txHash}`} target="_blank" rel="noreferrer"
                    style={{ display: "inline-flex", alignItems: "center", gap: 4, marginTop: 16, fontSize: 11, color: "#FFD700" }}>
                    Открыть в Polygonscan <ExternalLink size={11} />
                  </a>
                )}
              </>
            )}
            {status === "success" && !mining && (
              <>
                <CheckCircle2 size={48} color="#10F4A7" style={{ marginBottom: 14 }} />
                <div style={{ fontSize: 20, fontWeight: 800, color: "#10F4A7", marginBottom: 6 }}>Активация успешна!</div>
                <div style={{ fontSize: 13, color: "#8B94B8", marginBottom: 14 }}>
                  {tierDef.fee} USDT ({tierDef.label}): 56% → проект, 44% → 8-уровневая структура.<br />
                  <b>is_agent_active = true</b>. Airdrop €{tierDef.airdrop} → €{tierDef.payout} LOCKED_UNTIL_LAUNCH.
                </div>
                {txHash && (
                  <a href={`https://polygonscan.com/tx/${txHash}`} target="_blank" rel="noreferrer"
                    style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12, color: "#00F2FF", marginBottom: 16 }}>
                    <code>{shortAddr(txHash)}</code> <ExternalLink size={11} />
                  </a>
                )}
                <button onClick={() => setStatus(null)} data-testid="onboarding-close-success"
                  style={{ display: "block", margin: "0 auto", padding: "10px 22px", borderRadius: 8, background: "#10F4A7", color: "#050914", fontWeight: 700, border: "none", cursor: "pointer" }}>
                  Продолжить
                </button>
              </>
            )}
            {status === "error" && !mining && (
              <>
                <XCircle size={48} color="#FF3D6C" style={{ marginBottom: 14 }} />
                <div style={{ fontSize: 18, fontWeight: 800, color: "#FF3D6C", marginBottom: 6 }}>Ошибка транзакции</div>
                <div style={{ fontSize: 12, color: "#8B94B8", marginBottom: 16 }}>{errorMsg}</div>
                <button onClick={() => { setStatus(null); setErrorMsg(""); }}
                  data-testid="onboarding-close-error"
                  style={{ padding: "10px 22px", borderRadius: 8, background: "#FF3D6C", color: "#fff", fontWeight: 700, border: "none", cursor: "pointer" }}>
                  Закрыть
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {errorMsg && !status && (
        <div data-testid="onboarding-error-toast"
          style={{ position: "fixed", bottom: 20, left: "50%", transform: "translateX(-50%)", background: "#FF3D6C", color: "#fff", padding: "12px 18px", borderRadius: 10, fontSize: 13, zIndex: 90, maxWidth: 500 }}>
          ⚠ {errorMsg}
          <button onClick={() => setErrorMsg("")} style={{ marginLeft: 12, background: "transparent", border: "none", color: "#fff", cursor: "pointer", fontSize: 16 }}>×</button>
        </div>
      )}

      <style>{`.spin { animation: spin 1s linear infinite; } @keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </main>
  );
}

function StatCard({ label, value, color }) {
  return (
    <div style={{ padding: 14, background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 10 }}>
      <div style={{ fontSize: 11, color: "#8B94B8", textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color, fontFeatureSettings: '"tnum"' }}>{value}</div>
    </div>
  );
}
